import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import usePermissions from '../../../../../hooks/usePermissions'

import { insertIf } from '../../../../../helpers/common'
import { showToasts } from '../../../../../helpers/toasts'
import { isEditFormDisabled, isExistingPostTypeAd } from '../../../../../forms/Facebook/AdForms/AdFacebookEdit/helpers'

import { openForm } from '../../../../../modules/actions/forms'
import { approveInternalAd, clearUpdateAd, setSelectedAdForCopy, updateAd } from '../../../../../modules/actions/ads'
import { selectedAdAccountsSelector } from '../../../../../modules/selectors/adAccounts'
import { adUpdateErrorSelector, adWasUpdatedSelector } from '../../../../../modules/selectors/ads'

import { AD_FACEBOOK_EDIT, CHANGE_ADS_HISTORY } from '../../../../../constants/forms'
import { listItemChangeStatus, TOAST_TYPE } from '../../../../../constants/other'
import { FACEBOOK_PLATFORM, PROVIDER_TO_PLATFORMS } from '../../../../../constants/selectLists/platformList'

export const APPROVE_INTERNAL_AD_OPTION = 'APPROVE_INTERNAL_AD_OPTION'
export const EDIT_AD_OPTION = 'EDIT_AD_OPTION'
export const COPY_AD_OPTION = 'COPY_AD_OPTION'
export const VIEW_PREVIEW_ON_FACEBOOK_OPTION = 'VIEW_PREVIEW_ON_FACEBOOK_OPTION'
export const TOGGLE_STATUS_OPTION = 'TOGGLE_STATUS_OPTION'
export const DELETE_AD_OPTION = 'DELETE_AD_OPTION'
export const CHANGE_HISTORY_OPTION = 'CHANGE_HISTORY_OPTION'

export default function useFacebookAdActions({ ad, handleDeleteRequest, supportedActions }) {
  const dispatch = useDispatch()
  const adUpdateError = useSelector(adUpdateErrorSelector)
  const adUpdateWasUpdated = useSelector(adWasUpdatedSelector)

  const permissions = usePermissions()

  // extra state to manage only status updating (active/pause) and not e.g. edit form updating for this type of items
  const [isStatusUpdateRunning, setIsStatusUpdateRunning] = useState(false)

  const { [FACEBOOK_PLATFORM]: selectedAdAccount = {} } = useSelector(selectedAdAccountsSelector)

  const isInternalAd = ad.internal

  const { id: adId } = ad

  const defaultShowActions = useMemo(() => {
    return {
      [APPROVE_INTERNAL_AD_OPTION]: !isEditFormDisabled(ad) && permissions.can('manage', 'InternalAds'),
      [EDIT_AD_OPTION]: !isEditFormDisabled(ad) && permissions.can('update', 'Ad'),
      [COPY_AD_OPTION]: permissions.can('create', 'Ad') && !isExistingPostTypeAd(ad),
      [VIEW_PREVIEW_ON_FACEBOOK_OPTION]: true,
      [TOGGLE_STATUS_OPTION]: permissions.can('update', 'Ad'),
      [DELETE_AD_OPTION]: permissions.can('delete', 'Ad'),
      [CHANGE_HISTORY_OPTION]: true
    }
  }, [ad, permissions])

  const getShowAction = useCallback(
    action => {
      return supportedActions ? supportedActions[action] && defaultShowActions[action] : defaultShowActions[action]
    },
    [defaultShowActions, supportedActions]
  )

  const showActions = useMemo(() => {
    return {
      [APPROVE_INTERNAL_AD_OPTION]: getShowAction(APPROVE_INTERNAL_AD_OPTION),
      [EDIT_AD_OPTION]: getShowAction(EDIT_AD_OPTION),
      [COPY_AD_OPTION]: getShowAction(COPY_AD_OPTION),
      [VIEW_PREVIEW_ON_FACEBOOK_OPTION]: getShowAction(VIEW_PREVIEW_ON_FACEBOOK_OPTION),
      [TOGGLE_STATUS_OPTION]: getShowAction(TOGGLE_STATUS_OPTION),
      [DELETE_AD_OPTION]: getShowAction(DELETE_AD_OPTION),
      [CHANGE_HISTORY_OPTION]: getShowAction(CHANGE_HISTORY_OPTION)
    }
  }, [getShowAction])

  const copyAdHandler = useCallback(
    itemData => {
      // Copy Ad is saving in redux and is reset on AdAccount change
      // it allows to represent paste options in LineItemsList item actions dropdown or on LineItem summary page
      dispatch(setSelectedAdForCopy(itemData))

      showToasts({
        type: TOAST_TYPE.success,
        message: `'${itemData.name}' ad was copied successfully. You can now paste it into an ad set.`
      })
    },
    [dispatch]
  )

  const approveInternalAdHandler = useCallback(() => {
    dispatch(approveInternalAd(adId))
  }, [dispatch, adId])

  const editInternalAdHandler = useCallback(() => {
    dispatch(openForm({ id: adId, formName: AD_FACEBOOK_EDIT, otherParams: 'internal=true' }))
  }, [dispatch, adId])

  const actionsDropdownOptions = useMemo(
    () => [
      // internal approve/edit
      ...insertIf(
        isInternalAd,
        ...insertIf(showActions[APPROVE_INTERNAL_AD_OPTION], {
          text: 'Approve',
          onClickHandler: approveInternalAdHandler
        }),
        ...insertIf(showActions[EDIT_AD_OPTION], {
          text: 'Edit',
          onClickHandler: editInternalAdHandler
        })
      ),
      // internal/usual ad copy
      ...insertIf(showActions[COPY_AD_OPTION], {
        text: 'Copy',
        onClickHandler: () => copyAdHandler(ad)
      }),
      // usual ad actions
      ...insertIf(
        !isInternalAd,
        ...insertIf(showActions[EDIT_AD_OPTION], {
          text: 'Edit',
          onClickHandler: () => dispatch(openForm({ id: adId, formName: AD_FACEBOOK_EDIT }))
        }),
        ...insertIf(showActions[VIEW_PREVIEW_ON_FACEBOOK_OPTION], {
          text: 'View preview on Facebook',
          link: ad.preview,
          isItemLinkExternal: true
        }),
        ...insertIf(showActions[TOGGLE_STATUS_OPTION], {
          text: ad.status === 'active' ? 'Pause' : 'Activate',
          onClickHandler: () => {
            setIsStatusUpdateRunning(true)

            dispatch(
              updateAd(
                {
                  status: listItemChangeStatus[ad.status].changeTo,
                  account: selectedAdAccount.id
                },
                adId,
                FACEBOOK_PLATFORM
              )
            )
          }
        })
      ),
      ...insertIf(showActions[DELETE_AD_OPTION], {
        text: 'Delete',
        onClickHandler: () => {
          handleDeleteRequest({
            item: ad,
            account: ad.ad_account_id,
            platform: PROVIDER_TO_PLATFORMS[ad.provider]
          })
        }
      }),
      ...insertIf(showActions[CHANGE_HISTORY_OPTION], {
        text: 'Change History',
        onClickHandler: () => dispatch(openForm({ id: adId, formName: CHANGE_ADS_HISTORY }))
      })
    ],
    [
      ad,
      adId,
      approveInternalAdHandler,
      copyAdHandler,
      dispatch,
      editInternalAdHandler,
      isInternalAd,
      selectedAdAccount.id,
      handleDeleteRequest,
      showActions
    ]
  )

  useEffect(() => {
    if (isStatusUpdateRunning && (adUpdateWasUpdated || adUpdateError)) {
      setIsStatusUpdateRunning(false)

      dispatch(clearUpdateAd())
    }
  }, [dispatch, adUpdateError, adUpdateWasUpdated, isStatusUpdateRunning])

  return useMemo(
    () => ({
      actionsDropdownOptions,
      approveInternalAdHandler,
      editInternalAdHandler
    }),
    [actionsDropdownOptions, approveInternalAdHandler, editInternalAdHandler]
  )
}
